import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Video, Shield, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JITSI_DOMAIN = "meet.jit.si";
const ICON_URL = "https://customer-assets.emergentagent.com/job_online-pflegekasse/artifacts/72f9n7x9_1775301634776.png";

export default function VideoJoinPage() {
  const { roomId } = useParams();
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [name, setName] = useState("");
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRoom();
    return () => { if (apiRef.current) apiRef.current.dispose(); };
  }, [roomId]);

  const loadRoom = async () => {
    try {
      const res = await axios.get(`${API}/video/room/${roomId}`);
      setRoomInfo(res.data);
    } catch { setError("Dieser Video-Raum existiert nicht oder der Termin wurde storniert."); }
    setLoading(false);
  };

  const joinCall = () => {
    if (!name.trim()) return;

    const scriptId = "jitsi-external-api";
    const loadScript = () =>
      new Promise((resolve, reject) => {
        if (window.JitsiMeetExternalAPI) return resolve();
        if (document.getElementById(scriptId)) {
          const check = setInterval(() => {
            if (window.JitsiMeetExternalAPI) { clearInterval(check); resolve(); }
          }, 100);
          return;
        }
        const script = document.createElement("script");
        script.id = scriptId;
        script.src = `https://${JITSI_DOMAIN}/external_api.js`;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });

    loadScript().then(() => {
      apiRef.current = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
        roomName: roomId,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: { displayName: name },
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinPageEnabled: false,
          disableDeepLinking: true,
          defaultLanguage: "de",
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          DEFAULT_BACKGROUND: '#F8FAFC',
          TOOLBAR_ALWAYS_VISIBLE: true,
          MOBILE_APP_PROMO: false,
        },
      });
      apiRef.current.addListener("videoConferenceJoined", () => setJoined(true));
      apiRef.current.addListener("readyToClose", () => setJoined(false));
    });
  };

  const formatDate = (d) => {
    try { return new Date(d + "T00:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" }); }
    catch { return d; }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-cyan-700 border-t-transparent rounded-full" /></div>;

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center">
        <Video className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">Raum nicht gefunden</h2>
        <p className="text-sm text-slate-500">{error}</p>
        <a href="/" className="inline-block mt-6 text-sm text-cyan-700 hover:underline">Zur Website</a>
      </div>
    </div>
  );

  if (joined) return (
    <div className="h-screen w-screen bg-slate-900" data-testid="customer-video-active">
      <div ref={containerRef} className="h-full w-full" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="video-join-page">
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-lg p-8 max-w-md w-full text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-9 w-9 shrink-0 overflow-hidden">
            <img src={ICON_URL} alt="Logo" className="h-full w-auto object-cover object-left scale-[2.3] origin-[15%_41%]" />
          </div>
          <div className="text-left">
            <span className="text-sm font-bold text-slate-800 font-['Manrope'] block">Lehnen & Weinacht</span>
            <span className="text-[9px] text-cyan-600 font-semibold tracking-wider uppercase">Pflegeberatung & Alltagsbegleitung</span>
          </div>
        </div>

        <div className="h-14 w-14 bg-cyan-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <Video className="h-7 w-7 text-cyan-700" />
        </div>

        <h1 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">
          Video-Beratung beitreten
        </h1>

        {roomInfo && (
          <div className="bg-slate-50 rounded-xl p-4 mb-6 text-sm text-slate-600">
            <p className="font-medium">{formatDate(roomInfo.date)} — {roomInfo.time_slot} Uhr</p>
            <p className="text-xs text-slate-400 mt-1">Pflegeberatung nach §37 Abs. 3 SGB XI</p>
          </div>
        )}

        <div className="mb-4">
          <Input
            data-testid="join-name-input"
            placeholder="Ihr Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-12 rounded-xl text-center text-base"
            onKeyDown={(e) => e.key === "Enter" && name.trim() && joinCall()}
          />
        </div>

        <Button
          data-testid="join-video-btn"
          onClick={joinCall}
          disabled={!name.trim()}
          className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 text-base font-semibold disabled:opacity-50"
        >
          <Video className="h-5 w-5 mr-2" /> Jetzt beitreten
        </Button>

        <div className="flex items-center justify-center gap-2 mt-6 text-xs text-slate-400">
          <Shield className="h-3.5 w-3.5" />
          <span>Verschlüsselte Verbindung — DSGVO-konform</span>
        </div>
      </div>
    </div>
  );
}
